'use client';

import Icon from '@/components/ui/AppIcon';

interface Genre {
  id: string;
  name: string;
  count: number;
  icon: string;
  color: string;
}

const genres: Genre[] = [
  { id: 'genre_fiction', name: 'Fiction', count: 52, icon: 'BookOpenIcon', color: 'from-primary/20 to-primary/5' },
  { id: 'genre_mystery', name: 'Mystery', count: 38, icon: 'MagnifyingGlassIcon', color: 'from-accent/20 to-accent/5' },
  { id: 'genre_fantasy', name: 'Fantasy', count: 44, icon: 'SparklesIcon', color: 'from-success/20 to-success/5' },
  { id: 'genre_scifi', name: 'Science Fiction', count: 31, icon: 'BeakerIcon', color: 'from-primary/20 to-primary/5' },
  { id: 'genre_romance', name: 'Romance', count: 27, icon: 'HeartIcon', color: 'from-accent/20 to-accent/5' },
  { id: 'genre_thriller', name: 'Thriller', count: 23, icon: 'BoltIcon', color: 'from-warning/20 to-warning/5' },
  { id: 'genre_historical', name: 'Historical', count: 19, icon: 'ClockIcon', color: 'from-primary/20 to-primary/5' },
  { id: 'genre_nonfiction', name: 'Non-Fiction', count: 13, icon: 'AcademicCapIcon', color: 'from-accent/20 to-accent/5' },
];

interface GenreCardsProps {
  onGenreSelect: (genre: string) => void;
}

export default function GenreCards({ onGenreSelect }: GenreCardsProps) {
  return (
    <section className="py-20 px-6 bg-card/30">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-semibold text-foreground mb-4 tracking-tight">
            Explore by Genre
          </h2>
          <p className="text-muted-foreground font-light max-w-2xl mx-auto">
            Dive into curated collections across 15+ literary genres
          </p>
        </div>

        {/* Horizontal Scroll Container */}
        <div className="relative">
          <div className="flex gap-6 overflow-x-auto pb-6 scrollbar-hide snap-x snap-mandatory">
            {genres.map((genre) => (
              <button
                key={genre.id}
                onClick={() => onGenreSelect(genre.name)}
                className="flex-shrink-0 w-64 snap-center group"
              >
                <div className={`relative h-48 rounded-xl overflow-hidden bg-gradient-to-br ${genre.color} border border-border hover:border-primary/30 transition-all duration-300`}>
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-5">
                    <div className="absolute inset-0" style={{
                      backgroundImage: 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)',
                      backgroundSize: '32px 32px'
                    }} />
                  </div>

                  {/* Content */}
                  <div className="relative h-full p-6 flex flex-col justify-between">
                    <div className="w-12 h-12 rounded-xl bg-background/50 backdrop-blur-sm border border-border flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Icon name={genre.icon as any} size={24} className="text-foreground" />
                    </div>

                    <div>
                      <h3 className="text-xl font-serif font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                        {genre.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {genre.count} books reviewed
                      </p>
                    </div>
                  </div>

                  {/* Hover Arrow */}
                  <div className="absolute top-6 right-6 w-8 h-8 rounded-full bg-primary/0 group-hover:bg-primary flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                    <Icon name="ArrowRightIcon" size={16} className="text-primary-foreground" />
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Scroll Hint */}
          <div className="flex justify-center mt-6 gap-2">
            {genres.map((_, index) => (
              <div
                key={`scroll_dot_${index}`}
                className="w-2 h-2 rounded-full bg-border"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}